import { useQueryClient } from '@tanstack/react-query';
import { webRoutes } from '@runhuman/shared';
import type { DiscordActivityLinkedUser } from '@runhuman/shared';
import { WEB_BASE_URL } from '../web-base-url';
import { ACTIVITY_SESSION_QUERY_KEY } from '../use-activity-session';
import { openInBrowser } from './tester/harness';
import { ExternalLinkIcon } from '../ExternalLinkIcon';

/**
 * Same destination `<UnlinkedView>` opens — there's one identity-management
 * surface (web account settings), reachable from two Activity entry points:
 * the unlinked onboarding screen (first-link) and this header (re-link).
 */
const ACCOUNT_SETTINGS_URL = new URL(
  webRoutes.settingsAccount.build(),
  WEB_BASE_URL,
).toString();

interface Props {
  readonly linkedUser: DiscordActivityLinkedUser;
}

/**
 * Compact identity strip rendered above every authorized Activity view
 * (#3971). Surfaces the linked Runhuman alias (or email when no tester
 * profile) so users who maintain multiple accounts can tell at a glance
 * which Runhuman identity they're currently acting as. The "Not you?"
 * affordance hands off to the web account-settings page for re-linking —
 * the Activity itself never tries to swap identities (the web flow stays
 * the source of truth, per the #3971 non-goals).
 */
export function ActivityIdentityHeader({ linkedUser }: Props) {
  const queryClient = useQueryClient();

  const handleRelink = () => {
    void openInBrowser(ACCOUNT_SETTINGS_URL);
    // Invalidate the cached session so the next focus event (when the user
    // returns from the browser tab) refetches the now-changed linked
    // identity instead of returning the stale prior `linkedUser` (#4032).
    void queryClient.invalidateQueries({ queryKey: [ACTIVITY_SESSION_QUERY_KEY] });
  };

  return (
    <div className="activity-identity-header">
      <span className="activity-identity-header__label">
        You are{' '}
        <strong className="activity-identity-header__name">
          {linkedUser.displayName}
        </strong>{' '}
        on Runhuman
      </span>
      <button
        type="button"
        className="activity-identity-header__relink"
        onClick={handleRelink}
      >
        Not you?
        <ExternalLinkIcon />
      </button>
    </div>
  );
}
